/**
 * CivicPulse — placeholder pages that still fit the CivicPulse chrome:
 *   /civic/resolved      → recently resolved feed
 *   /civic/communities   → participating communities
 *   /civic/authorities   → gov / authority onboarding
 *
 * These are lightweight so the primary nav feels complete. Real content
 * arrives with the "b" (Authority accounts) and "c" (Verification) work.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Building2, Users, ArrowRight, MapPin } from "lucide-react";
import { civic, photoUrl } from "./api";
import { civicPath } from "./basePath";

export function CivicResolved() {
  const [items, setItems] = useState([]);
  useEffect(() => { civic.recentResolved(24).then((d) => setItems(d.items || [])); }, []);
  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="civic-resolved-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900">
          <CheckCircle2 className="inline w-6 h-6 -mt-1 mr-2 text-emerald-700" />
          Recently Resolved
        </h1>
        <p className="text-slate-600 mt-1 mb-6 text-sm">
          Problems the community has confirmed as truly fixed. Each one is a small step toward a stronger city.
        </p>
        {items.length === 0 ? (
          <div className="rounded-2xl bg-white ring-1 ring-emerald-100 p-10 text-center text-slate-500 italic">
            The first community-verified resolutions will appear here.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((it) => (
              <Link to={civicPath(`/issue/${it.public_id}`)} key={it.public_id}
                    className="rounded-2xl bg-white ring-1 ring-emerald-100 hover:ring-emerald-300 hover:shadow transition overflow-hidden">
                {it.photo_urls?.[0] && (
                  <img src={photoUrl(it.photo_urls[0])} alt="" className="w-full h-40 object-cover" />
                )}
                <div className="p-4">
                  <div className="text-[11px] uppercase tracking-widest font-semibold text-emerald-700">✓ Verified resolved</div>
                  <div className="font-semibold text-slate-800 mt-1">{it.title}</div>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {it.location?.address || it.location?.city || "Kerala"}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function CivicCommunities() {
  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900">
          <Users className="inline w-6 h-6 -mt-1 mr-2 text-emerald-700" /> Communities
        </h1>
        <p className="text-slate-600 mt-1 text-sm max-w-2xl">
          CivicPulse is piloting in Kerala first. Explore any place to see local issues, response times, and volunteer opportunities.
        </p>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {["Kottayam", "Ernakulam", "Thiruvananthapuram", "Kozhikode", "Thrissur", "Kasaragod"].map((d) => (
            <Link key={d} to={civicPath(`/india/kerala/${d.toLowerCase()}`)}
                  className="rounded-2xl bg-white ring-1 ring-emerald-100 hover:ring-emerald-300 hover:shadow p-5 flex items-start gap-3 transition">
              <span className="grid place-items-center w-11 h-11 rounded-full bg-emerald-100 text-emerald-700 shrink-0">
                <MapPin className="w-5 h-5" />
              </span>
              <div>
                <div className="font-semibold text-slate-800">{d}</div>
                <div className="text-xs text-slate-500">Kerala, India</div>
                <div className="text-xs text-emerald-700 mt-2 font-semibold inline-flex items-center gap-1">
                  Explore <ArrowRight className="w-3 h-3" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function CivicAuthorities() {
  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
        <div className="text-xs font-semibold uppercase tracking-[.22em] text-emerald-700 mb-2">For Officials & Authorities</div>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900">
          <Building2 className="inline w-6 h-6 -mt-1 mr-2 text-emerald-700" /> Authority dashboards
        </h1>
        <p className="text-slate-600 mt-3 leading-relaxed">
          Panchayats, municipalities, corporations, PWD, KWA, KSEB and other
          authorities can claim a verified account to receive assigned issues,
          respond publicly, and upload before-and-after evidence.
        </p>
        <div className="mt-6 rounded-2xl bg-white ring-1 ring-emerald-100 p-5">
          <div className="font-semibold text-slate-800">Coming next</div>
          <ul className="mt-2 space-y-1.5 text-sm text-slate-600">
            <li>• Verified organizational accounts</li>
            <li>• Assigned-issue inbox with ageing queue</li>
            <li>• CPGRAMS & state grievance-portal routing</li>
            <li>• Public performance scorecards</li>
          </ul>
          <p className="mt-4 text-xs text-slate-500">
            Want to onboard early? Email <a className="underline text-emerald-700" href="mailto:hello@civicpulse.blog">hello@civicpulse.blog</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
